import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ModalBackdrop,
  ModalContainer,
  ModalContent,
  ModalTitle,
  ModalDescription,
  PhoneNumber,
  CodeInputContainer,
  CodeInput,
  ErrorMessage,
  ErrorIcon,
  ResendButton,
  ButtonContainer,
  CancelButton,
  VerifyButton,
  LoadingSpinner,
  ScreenReaderOnly,
} from './TwoFactorModal.styles';

const TwoFactorModal = ({
  isOpen = true,
  onClose = () => {},
  onVerify = () => {},
  onResend = () => {},
  phoneNumber = '+31 6 76953549',
  isLoading = false,
  error = '',
  resendCooldown = 0,
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [hasError, setHasError] = useState(false);
  const inputRefs = useRef([]);
  const modalRef = useRef(null);

  // Focus trap for accessibility
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, input, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      if (firstElement) firstElement.focus();

      return () => {
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset error state when error prop changes
  useEffect(() => {
    if (error) {
      setHasError(true);
      if (inputRefs?.current[0]) inputRefs.current[0].focus();
    } else {
      setHasError(false);
    }
  }, [error]);

  // Define handleVerify first so it can be used in handleKeyDown
  const handleVerify = useCallback(() => {
    const fullCode = code.join('');
    if (fullCode.length === 6) {
      onVerify(fullCode);
    }
  }, [code, onVerify]);

  const handleInputChange = useCallback(
    (index, value) => {
      if (!/^\d*$/.test(value)) return;

      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      setHasError(false);

      if (value && index < 5) {
        if (inputRefs.current[index + 1]) {
          inputRefs.current[index + 1].focus();
        }
      }
    },
    [code]
  );

  const handleKeyDown = useCallback(
    (index, e) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        if (inputRefs.current[index - 1]) inputRefs.current[index - 1].focus();
      } else if (e.key === 'ArrowLeft' && index > 0) {
        if (inputRefs.current[index - 1]) inputRefs.current[index - 1].focus();
      } else if (e.key === 'ArrowRight' && index < 5) {
        if (inputRefs.current[index + 1]) inputRefs.current[index + 1].focus();
      } else if (e.key === 'Enter') {
        handleVerify();
      }
    },
    [code, handleVerify]
  );

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '');

    if (pastedData.length <= 6) {
      const newCode = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setCode(newCode);
      setHasError(false);

      const nextEmptyIndex = newCode.findIndex((digit) => !digit);
      const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
      if (inputRefs.current[focusIndex]) inputRefs.current[focusIndex].focus();
    }
  }, []);

  const handleResend = useCallback(() => {
    if (resendCooldown === 0) {
      onResend();
      setCode(['', '', '', '', '', '']);
      setHasError(false);
      if (inputRefs.current[0]) inputRefs.current[0].focus();
    }
  }, [resendCooldown, onResend]);

  const isCodeComplete = code.every((digit) => digit !== '');

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()} role="presentation">
      <ModalContainer ref={modalRef}>
        <ModalContent>
          <ScreenReaderOnly>
            Two-factor authentication modal opened. Enter the 6-digit code sent to your phone.
          </ScreenReaderOnly>

          <ModalTitle id="modal-title">Two-step authentication</ModalTitle>

          <ModalDescription id="modal-description">
            Enter the authentication code below we sent to <PhoneNumber>{phoneNumber}</PhoneNumber>.
          </ModalDescription>

          <CodeInputContainer>
            {code.map((digit, index) => (
              <CodeInput
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                value={digit}
                index={index}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={`${hasError ? 'error' : ''} ${digit ? 'filled' : ''}`}
                disabled={isLoading}
                aria-invalid={hasError}
                aria-describedby={error ? 'error-message' : undefined}
              />
            ))}
          </CodeInputContainer>

          <ErrorMessage id="error-message">
            {error && (
              <>
                <ErrorIcon aria-hidden="true" />
                {error}
              </>
            )}
          </ErrorMessage>

          <ResendButton
            onClick={handleResend}
            disabled={resendCooldown > 0 || isLoading}
            aria-live="polite"
          >
            {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
          </ResendButton>

          <ButtonContainer>
            <CancelButton onClick={onClose} disabled={isLoading} type="button">
              Cancel
            </CancelButton>

            <VerifyButton
              onClick={handleVerify}
              disabled={!isCodeComplete || isLoading}
              type="button"
              aria-describedby="verify-button-description"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner aria-hidden="true" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </VerifyButton>

            <ScreenReaderOnly id="verify-button-description">
              {!isCodeComplete
                ? 'Enter all 6 digits to enable verification'
                : 'All digits entered, ready to verify'}
            </ScreenReaderOnly>
          </ButtonContainer>
        </ModalContent>
      </ModalContainer>
    </ModalBackdrop>
  );
};

export default TwoFactorModal;
